import { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Divider, ListItem } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { ListCell, ListHeader, ListRow, ViewNotesModal } from 'src/components';
import { useAdvanceStore } from '/src/context';
import { mediumDate } from '/src/lib/constants';
import { Advance, Note } from '/src/lib/models';
import { TListAdvancesRequest, Totals } from '/src/lib/types';
import * as utils from '/src/utils';

export type AdvancesListProps = {
  /**
   * List of Advances to display
   */
  data?: Advance[];
  /**
   * Query to include when fetching Advances
   */
  query?: TListAdvancesRequest;
};

/**
 * Advances List
 */
export const AdvancesList: React.FC<AdvancesListProps> = observer(({ data, query }) => {
  const defaultSortType = 'sequenceNumber';
  const defaultSortReverse = false;
  const rowHeight = 48;

  /** Context **/
  const advanceStore = useAdvanceStore();

  /** State **/
  const [advances, setAdvances] = useState<Advance[]>([]);
  const [totals, setTotals] = useState<Totals>({});
  const [resolved, setResolved] = useState(false);
  const [sortType, setSortType] = useState(defaultSortType);
  const [sortReverse, setSortReverse] = useState(defaultSortReverse);

  // View Notes popup shows if `notes` is defined
  const [notes, setNotes] = useState<Note[]>();
  const [notesName, setNotesName] = useState<string>();

  /** Computed **/

  const listData = useMemo(() => {
    if (advances) {
      const list = advances.toSorted((a, b) => ((a as any)[sortType] > (b as any)[sortType] ? 1 : -1));
      if (sortReverse) list.reverse();

      return list;
    } else return [];
  }, [advances, sortType, sortReverse]);

  /** Methods **/

  const formatData = (data: Advance[]) => {
    // Prepare totals
    const totals: Totals = {};
    totals.amount = 0;
    totals.offset = 0;
    totals.originationFee = 0;

    const advances = data.map((advance) => {
      // Calculate totals
      totals.amount += advance.amount;
      if (advance.originationFee) totals.originationFee += advance.originationFee;

      // Store
      return advance;
    });

    // Save data
    setAdvances(advances);
    setTotals(totals);

    // Set as resolved
    setResolved(true);
  };

  const showNotesPopup = (notes?: Note[], name?: string) => {
    setNotes(notes ?? []);
    setNotesName(name);
  };

  /**
   * Sets sortType and sortReverse based on given sortType
   * (From ListDirectiveController)
   * @param type string sortType to use
   */
  const sortList = (type?: string) => {
    if (type) {
      if (sortType === type) setSortReverse(!sortReverse);
      else setSortType(type);
    }
  };

  /** Effects **/

  useEffect(() => {
    if (data) {
      formatData(data);
    } else if (query && Object.values(query).length) {
      advanceStore.query(query).then((advances) => advances && formatData(advances));
    }
  }, [data, query]);

  /** Render **/
  return (
    <Box>
      {/* HEADER */}
      {!!listData.length && (
        <ListHeader
          items={[
            { flex: 5, propName: 'notes', label: 'Notes' },
            { propName: 'name', label: 'Name' },
            { propName: 'amount', label: 'Amount' },
            { propName: 'offset', label: 'Offset' },
            { propName: 'originationFee', label: 'Origination Fee' },
            { propName: 'loanName', label: 'Deal Name' },
            { propName: 'dateRequested', label: 'Requested On' },
            { propName: 'dateFulfilled', label: 'Fulfilled On' },
          ]}
          sortType={sortType}
          sortReverse={sortReverse}
          sortList={sortList}
        />
      )}

      {!!listData.length && <Divider />}

      {/* LIST */}
      {resolved && !!listData.length && (
        <Box component="md-content" height={listData.length * rowHeight + 'px'} maxHeight={9 * rowHeight + 'px'}>
          {listData.map((advance, i) => (
            <ListRow
              key={advance._id}
              padForScrollbar={listData.length > 8}
              items={[
                {
                  propName: 'notes',
                  isNotes: true,
                  flex: 5,
                  hide: !advance.notes.length,
                  showNotesPopup: () => showNotesPopup(advance.notes, advance.name),
                },
                { propName: 'name', content: advance.name, url: `/advances/${advance._id}` },
                { propName: 'amount', content: utils.currency.format(advance.amount) },
                { propName: 'offset', content: advance.offset ? utils.currency.format(advance.offset) : '' },
                {
                  propName: 'originationFee',
                  content: advance.originationFee ? utils.currency.format(advance.originationFee) : '',
                },
                { propName: 'loanName', content: advance.loan.name, url: `/deals/${advance.loan._id}` },
                { propName: 'dateRequested', content: dayjs(advance.dateRequested).format(mediumDate) },
                { propName: 'dateFulfilled', content: dayjs(advance.dateFulfilled).format(mediumDate) },
              ]}
              rowIndex={i}
            />
          ))}
        </Box>
      )}

      <Divider />

      {/* TOTALS */}
      {!!listData.length && (
        <ListItem sx={{ height: rowHeight }}>
          <Box
            display="flex"
            flex="100"
            justifyContent="space-between"
            alignItems="center"
            className={`medium md-subhead-sm text-center ${listData.length > 8 && 'padForScrollbar'}`}
          >
            {[
              { flex: 5, propName: 'notes', label: 'Notes' },
              { propName: 'name', label: 'Name' },
              { propName: 'amount', label: 'Amount' },
              { propName: 'offset', label: 'Offset' },
              { propName: 'originationFee', label: 'Origination Fee' },
              { propName: 'loanName', label: 'Deal Name' },
              { propName: 'dateRequested', label: 'Requested On' },
              { propName: 'dateFulfilled', label: 'Fulfilled On' },
            ].map(({ propName, ...itemProps }, i) => (
              <ListCell
                key={`${propName}-${i}`}
                content={i === 0 ? 'Totals' : totals[propName] ? utils.currency.format(totals[propName]) : ''}
                {...itemProps}
              />
            ))}
          </Box>
        </ListItem>
      )}

      {resolved && !listData.length && (
        <ListItem>
          <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
            <Box component="span">No Advances found.</Box>
          </Box>
        </ListItem>
      )}

      {!resolved && (
        <ListItem>
          <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
            <CircularProgress className="md-primary" md-mode="indeterminate" />
          </Box>
        </ListItem>
      )}

      {notes && (
        <ViewNotesModal
          notes={notes}
          name={notesName}
          open={!!notes}
          setOpen={(open) => !open && setNotes(undefined)}
        />
      )}
    </Box>
  );
});
